/**
 * WeaveIn.ai Platform - Workflow Instance Tracker Component
 * Monitors and displays active workflow instances with real-time status
 * Includes both standard workflows and employer agentic workflows
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useWorkflowInstances } from '@/hooks/useWorkflows';
import useWorkflowTemplates from '@/hooks/useWorkflows';
import { WorkflowInstance, WorkflowInstanceStatus, workflowsAPI } from '@/services/api/workflows';
import { onOpenRecord, takePendingRecord } from '@/lib/recordDeepLinks';
import { useNotificationTriggers } from '@/hooks/useNotificationTriggers';
import {
  getEmployerAgenticWorkflows,
  EmployerAgenticWorkflow
} from '@/services/api/aiOrchestration';
import { MemberWorkflowProgress } from './MemberWorkflowProgress';
import { List, LayoutGrid, Pause, XCircle, PlayCircle, RefreshCw } from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';

// Extended interface to include agentic workflow indicator
interface ExtendedWorkflowInstance extends WorkflowInstance {
  is_agentic?: boolean;
  employer_name?: string;
  template_type?: string;
}
interface WorkflowInstanceTrackerProps {
  onNavigate?: (tab: string, instanceId?: number) => void;
  pageSize?: number;
}

export const WorkflowInstanceTracker: React.FC<WorkflowInstanceTrackerProps> = ({
  onNavigate,
  pageSize = 50,
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [selectedTemplate, setSelectedTemplate] = useState<number | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<WorkflowInstanceStatus | undefined>();
  const [selectedWorkflowType, setSelectedWorkflowType] = useState<'all' | 'standard' | 'agentic'>('all');
  const [viewMode, setViewMode] = useState<'list' | 'visual'>('list');
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const { notifyWorkflowCompletion } = useNotificationTriggers();

  // TRUS-685: /workflow-instances?instance=<id> (a system-event link) focuses
  // one instance: a summary above the list (it may not be on this page of it)
  // and its row highlighted when it is.
  const [focusId, setFocusId] = useState<number | null>(() => takePendingRecord('workflow-instance')?.instanceId ?? null);
  const [focused, setFocused] = useState<WorkflowInstance | null>(null);
  const [focusError, setFocusError] = useState(false);
  useEffect(
    () =>
      onOpenRecord('workflow-instance', (link) => {
        setFocusId(link.instanceId);
        return true;
      }),
    [],
  );
  useEffect(() => {
    if (!focusId) return;
    let alive = true;
    setFocused(null);
    setFocusError(false);
    workflowsAPI
      .getWorkflowInstance(focusId)
      .then((inst) => alive && setFocused(inst))
      .catch(() => alive && setFocusError(true));
    return () => {
      alive = false;
    };
  }, [focusId]);

  // Employer agentic workflows state
  const [agenticWorkflows, setAgenticWorkflows] = useState<EmployerAgenticWorkflow[]>([]);
  const [agenticLoading, setAgenticLoading] = useState(false);
  const [agenticError, setAgenticError] = useState<string | null>(null);

  const {
    instances,
    total,
    page,
    loading,
    error,
    setPage,
    setFilters,
    updateInstance,
    deleteInstance,
  } = useWorkflowInstances({
    page_size: pageSize,
    workflow_template_id: selectedTemplate,
    status: selectedStatus,
  });

  const { templates = [] } = useWorkflowTemplates({ page_size: 100 }) || { templates: [] };

  // Map WorkflowInstanceStatus to agentic workflow status values
  const mapStatusToAgenticStatus = (status?: WorkflowInstanceStatus): string | undefined => {
    if (!status) return undefined;
    const statusMap: Record<WorkflowInstanceStatus, string | undefined> = {
      'running': undefined, // Fetch all running states: pending, queued, in_progress, waiting_response
      'completed': 'completed',
      'failed': 'escalated',
      'cancelled': 'cancelled',
      'paused': 'paused',
    };
    return statusMap[status];
  };

  // Fetch employer agentic workflows
  useEffect(() => {
    const fetchAgenticWorkflows = async () => {
      setAgenticLoading(true);
      setAgenticError(null);
      try {
        // Map the status to agentic workflow format
        const agenticStatus = mapStatusToAgenticStatus(selectedStatus);
        const workflows = await getEmployerAgenticWorkflows({
          status: agenticStatus,
          limit: pageSize
        });

        // If filtering for 'running', manually filter to include only running states
        let filteredWorkflows = workflows;
        if (selectedStatus === 'running') {
          filteredWorkflows = workflows.filter(w =>
            ['pending', 'queued', 'in_progress', 'waiting_response'].includes(w.status)
          );
        }

        setAgenticWorkflows(filteredWorkflows);
      } catch (err) {
        console.error('Error fetching agentic workflows:', err);
        setAgenticError('Failed to load agentic workflows');
      } finally {
        setAgenticLoading(false);
      }
    };

    fetchAgenticWorkflows();
  }, [selectedStatus, pageSize]);

  // Convert agentic workflows to ExtendedWorkflowInstance format
  const convertAgenticToInstance = (workflow: EmployerAgenticWorkflow): ExtendedWorkflowInstance => {
    // Map agentic workflow status to WorkflowInstanceStatus
    const statusMap: Record<string, WorkflowInstanceStatus> = {
      'pending': 'running',
      'queued': 'running',
      'in_progress': 'running',
      'waiting_response': 'running',
      'completed': 'completed',
      'cancelled': 'cancelled',
      'escalated': 'failed',
      'paused': 'paused',
      'failed': 'failed',
    };

    return {
      id: workflow.workflow_instance_id,
      workflow_template_id: workflow.id,
      enrollment_id: workflow.employee_id,
      cbo_id: 0,
      status: statusMap[workflow.status] || 'running',
      created_ts: workflow.initiated_at,
      last_updated_ts: workflow.completed_at || workflow.initiated_at,
      started_at: workflow.initiated_at,
      completed_at: workflow.completed_at,
      workflow_name: workflow.template_name,
      member_name: workflow.employee_name,
      program_name: workflow.employer_name,
      current_step_order: workflow.status === 'completed' ? 5 : workflow.status === 'in_progress' ? 2 : 1,
      is_agentic: true,
      employer_name: workflow.employer_name,
      template_type: workflow.template_type,
    };
  };

  // Merge and filter workflows based on type selection
  const allInstances: ExtendedWorkflowInstance[] = useMemo(() => {
    const standardInstances: ExtendedWorkflowInstance[] = instances.map(inst => ({
      ...inst,
      is_agentic: false
    }));
    const agenticInstances: ExtendedWorkflowInstance[] = agenticWorkflows.map(convertAgenticToInstance);

    let merged: ExtendedWorkflowInstance[] = [];

    if (selectedWorkflowType === 'standard') {
      merged = standardInstances;
    } else if (selectedWorkflowType === 'agentic') {
      merged = agenticInstances;
    } else {
      merged = [...standardInstances, ...agenticInstances];
    }

    // Sort by started_at descending (most recent first)
    let sorted = merged.sort((a, b) => {
      const dateA = new Date(a.started_at || a.created_ts).getTime();
      const dateB = new Date(b.started_at || b.created_ts).getTime();
      return dateB - dateA;
    });

    // Filter by member name if search query is provided
    if (memberSearchQuery.trim()) {
      const query = memberSearchQuery.toLowerCase().trim();
      sorted = sorted.filter(inst =>
        inst.member_name?.toLowerCase().includes(query) ||
        String(inst.enrollment_id).includes(query) ||
        String(inst.member_id).includes(query)
      );
    }

    return sorted;
  }, [instances, agenticWorkflows, selectedWorkflowType, memberSearchQuery]);

  const combinedTotal = total + agenticWorkflows.length;
  const isLoading = loading || agenticLoading;
  const combinedError = error || agenticError;

  const handleFilterChange = (templateId?: number, status?: WorkflowInstanceStatus) => {
    setSelectedTemplate(templateId);
    setSelectedStatus(status);
    setFilters({ workflow_template_id: templateId, status });
  };

  const handleStatusChange = async (instance: WorkflowInstance, newStatus: WorkflowInstanceStatus) => {
    const success = await updateInstance(instance.id, { status: newStatus });
    if (success) {
      alert(`Workflow instance status updated to ${newStatus}`);

      // Trigger desktop notification when workflow is completed
      if (newStatus === 'completed') {
        const template = templates.find(tmpl => tmpl.id === instance.workflow_template_id);
        const workflowName = template?.name || 'Workflow';
        const memberName = instance.member_id ? `Member #${instance.member_id}` : 'Unknown';

        await notifyWorkflowCompletion({
          workflowName,
          memberName,
          workflowId: instance.id,
          memberId: instance.member_id,
        });
      }
    }
  };

  const handleDelete = async (id: number, workflowName: string) => {
    if (window.confirm(`Are you sure you want to delete this workflow instance for "${workflowName}"?`)) {
      const success = await deleteInstance(id);
      if (success) {
        alert('Workflow instance deleted successfully');
      }
    }
  };

  const [propagatingId, setPropagatingId] = useState<number | null>(null);

  const handlePropagate = async (templateId: number, templateName: string) => {
    if (!window.confirm(
      `Propagate changes from "${templateName}" to all active instances?\n\nThis syncs step names, types, and creates document requirements for existing enrollments.`
    )) return;

    setPropagatingId(templateId);
    try {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      const baseUrl = (typeof window !== 'undefined' && (window as any).__WEAVEIN_API_URL) ||
        (typeof process !== 'undefined' && process.env?.VITE_WEAVEIN_API_URL) ||
        'https://weavein-api.onrender.com';
      const res = await fetch(`${baseUrl}/api/workflows/templates/${templateId}/propagate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId || '',
          'Content-Type': 'application/json',
        },
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Propagated to ${data.active_instances} instance(s):\n• ${data.steps_created || 0} new step(s) created\n• ${data.steps_synced || 0} step(s) synced\n• ${data.doc_requirements_created} doc requirement(s) created`);
      } else {
        const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
        alert(`Failed: ${err.detail}`);
      }
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    } finally {
      setPropagatingId(null);
    }
  };

  const getStatusBadgeClass = (status: WorkflowInstanceStatus) => {
    switch (status) {
      case 'running':
        return 'badge-info';
      case 'completed':
        return 'badge-success';
      case 'failed':
        return 'badge-danger';
      case 'cancelled':
        return 'badge-secondary';
      case 'paused':
        return 'badge-warning';
      default:
        return 'badge';
    }
  };

  const getStatusColor = (status: WorkflowInstanceStatus) => {
    switch (status) {
      case 'running':
        return '#3b82f6';
      case 'completed':
        return '#10b981';
      case 'failed':
        return '#ef4444';
      case 'cancelled':
        return '#6b7280';
      case 'paused':
        return '#f59e0b';
      default:
        return '#9ca3af';
    }
  };

  const totalPages = Math.ceil(combinedTotal / pageSize);

  // Calculate status statistics from combined instances
  const stats = {
    running: allInstances.filter((i) => i.status === 'running').length,
    completed: allInstances.filter((i) => i.status === 'completed').length,
    failed: allInstances.filter((i) => i.status === 'failed').length,
    paused: allInstances.filter((i) => i.status === 'paused').length,
    cancelled: allInstances.filter((i) => i.status === 'cancelled').length,
    agentic: allInstances.filter((i) => i.is_agentic).length,
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 p-6">
      {focusId && (
        <div
          className="mb-4 flex flex-wrap items-start justify-between gap-3 rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm"
          role="status"
          data-testid="workflow-instance-focus"
        >
          {focusError ? (
            <span>{t('workflows.focus.notFound', 'Workflow #{{id}} was not found, or you no longer have access to it.', { id: focusId })}</span>
          ) : !focused ? (
            <span className="text-muted-foreground">{t('workflows.focus.loading', 'Loading workflow #{{id}}…', { id: focusId })}</span>
          ) : (
            <div className="space-y-0.5">
              <div className="font-semibold">
                {focused.workflow_name || t('workflows.focus.untitled', 'Workflow #{{id}}', { id: focused.id })}
              </div>
              <div className="text-muted-foreground">
                {[focused.program_name, focused.member_name, focused.status].filter(Boolean).join(' · ')}
              </div>
            </div>
          )}
          <button
            type="button"
            className="text-xs text-muted-foreground underline"
            onClick={() => {
              setFocusId(null);
              setFocused(null);
            }}
          >
            {t('workflows.focus.dismiss', 'Dismiss')}
          </button>
        </div>
      )}
      {/* Toolbar (page title rendered by the shell chrome) */}
      <div className="flex flex-wrap items-center justify-end gap-2 mb-6">
        <div style={{
          display: 'inline-flex',
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          overflow: 'hidden',
          backgroundColor: '#f9fafb'
        }}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setViewMode('list');
            }}
            title={t('workflows.list_view', 'List View - Table format')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              border: 'none',
              borderRadius: 0,
              margin: 0,
              cursor: 'pointer',
              backgroundColor: viewMode === 'list' ? '#3b82f6' : 'transparent',
              color: viewMode === 'list' ? 'white' : '#374151',
              fontWeight: viewMode === 'list' ? 600 : 400,
              transition: 'all 0.15s ease'
            }}
          >
            <List className="w-4 h-4" />
            <span style={{ fontSize: '13px' }}>{t('workflows.list', 'List')}</span>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setViewMode('visual');
            }}
            title={t('workflows.visual_view', 'Visual View - Workflow progress cards')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              border: 'none',
              borderLeft: '1px solid #d1d5db',
              borderRadius: 0,
              margin: 0,
              cursor: 'pointer',
              backgroundColor: viewMode === 'visual' ? '#3b82f6' : 'transparent',
              color: viewMode === 'visual' ? 'white' : '#374151',
              fontWeight: viewMode === 'visual' ? 600 : 400,
              transition: 'all 0.15s ease'
            }}
          >
            <LayoutGrid className="w-4 h-4" />
            <span style={{ fontSize: '13px' }}>{t('workflows.visual', 'Visual')}</span>
          </button>
        </div>
      </div>

      {combinedError && (
        <div className="alert alert-error">
          <strong>{t('workflows.error', 'Error:')}</strong> {combinedError}
        </div>
      )}

      {/* Statistics Cards */}
      {allInstances.length > 0 && (
        <div className="mapping-guide">
          <div className="guide-content" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px' }}>
            <div className="guide-item" style={{ borderLeft: `4px solid ${getStatusColor('running')}` }}>
              <strong>{stats.running}</strong>
              <p>{t('workflows.running', 'Running')}</p>
            </div>
            <div className="guide-item" style={{ borderLeft: `4px solid ${getStatusColor('completed')}` }}>
              <strong>{stats.completed}</strong>
              <p>{t('workflows.completed', 'Completed')}</p>
            </div>
            <div className="guide-item" style={{ borderLeft: `4px solid ${getStatusColor('failed')}` }}>
              <strong>{stats.failed}</strong>
              <p>{t('workflows.failed', 'Failed')}</p>
            </div>
            <div className="guide-item" style={{ borderLeft: `4px solid ${getStatusColor('paused')}` }}>
              <strong>{stats.paused}</strong>
              <p>{t('workflows.paused', 'Paused')}</p>
            </div>
            <div className="guide-item" style={{ borderLeft: `4px solid ${getStatusColor('cancelled')}` }}>
              <strong>{stats.cancelled}</strong>
              <p>{t('workflows.cancelled', 'Cancelled')}</p>
            </div>
            <div className="guide-item" style={{ borderLeft: '4px solid #8b5cf6' }}>
              <strong>{stats.agentic}</strong>
              <p>{t('workflows.agentic', 'AI Agentic')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filter-bar" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center' }}>
        {/* Member Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label>{t('workflows.search_member', 'Search Member:')}</label>
          <input
            type="text"
            placeholder={t('workflows.member_name_or_id', 'Name or ID...')}
            value={memberSearchQuery}
            onChange={(e) => setMemberSearchQuery(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              width: '180px',
              fontSize: '14px'
            }}
          />
          {memberSearchQuery && (
            <button
              type="button"
              onClick={() => setMemberSearchQuery('')}
              style={{
                padding: '4px 8px',
                border: 'none',
                background: '#e5e7eb',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              {t('workflows.clear', 'Clear')}
            </button>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label>{t('workflows.workflow_type', 'Workflow Type:')}</label>
          <select
            value={selectedWorkflowType}
            onChange={(e) => setSelectedWorkflowType(e.target.value as 'all' | 'standard' | 'agentic')}
          >
            <option value="all">{t('workflows.all_workflows', 'All Workflows')}</option>
            <option value="standard">{t('workflows.standard_workflows', 'Standard Workflows')}</option>
            <option value="agentic">{t('workflows.agentic_workflows', 'AI Agentic Workflows')}</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label>{t('workflows.filter_by_template', 'Template:')}</label>
          <select
            value={selectedTemplate || ''}
            onChange={(e) => handleFilterChange(e.target.value ? parseInt(e.target.value) : undefined, selectedStatus)}
            disabled={selectedWorkflowType === 'agentic'}
          >
            <option value="">{t('workflows.all_templates', 'All Templates')}</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label>{t('workflows.status', 'Status:')}</label>
          <select
            value={selectedStatus || ''}
            onChange={(e) => handleFilterChange(selectedTemplate, e.target.value as WorkflowInstanceStatus || undefined)}
          >
            <option value="">{t('workflows.all_statuses', 'All Statuses')}</option>
            <option value="running">{t('workflows.running_1', 'Running')}</option>
            <option value="completed">{t('workflows.completed_1', 'Completed')}</option>
            <option value="failed">{t('workflows.failed_1', 'Failed')}</option>
            <option value="paused">{t('workflows.paused_1', 'Paused')}</option>
            <option value="cancelled">{t('workflows.cancelled_1', 'Cancelled')}</option>
          </select>
        </div>
      </div>

      {isLoading && allInstances.length === 0 ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t('workflows.loading_workflow_instances', 'Loading workflow instances...')}</p>
        </div>
      ) : viewMode === 'visual' ? (
        /* Visual View - Card-based with MemberWorkflowProgress */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '20px', padding: '20px 0' }}>
          {allInstances.length === 0 ? (
            <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
              <h3>{t('workflows.no_workflow_instances_found', 'No workflow instances found')}</h3>
              <p>
                {selectedTemplate || selectedStatus || selectedWorkflowType !== 'all'
                  ? t('workflows.no_instances_match_filters', 'No instances match the current filters.')
                  : t('workflows.workflow_instances_will_appear', 'Workflow instances will appear here when workflows are triggered.')}
              </p>
            </div>
          ) : (
            allInstances.filter(inst => !inst.is_agentic).map((instance) => (
              <div key={`visual-${instance.id}`} style={{ minHeight: '200px' }}>
                {/* Member Header Card */}
                <div style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderBottom: 'none',
                  borderRadius: '8px 8px 0 0',
                  padding: '12px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '15px', color: '#1e293b' }}>
                      {instance.member_name || `Member #${instance.member_id || instance.enrollment_id}`}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                      {instance.program_name && <span>{instance.program_name}</span>}
                      {instance.program_name && instance.enrollment_id && <span> • </span>}
                      <span>Enrollment #{instance.enrollment_id}</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span className={`badge ${getStatusBadgeClass(instance.status)}`}>
                      {instance.status}
                    </span>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                      {fmt.short(instance.started_at || instance.created_ts)}
                    </div>
                  </div>
                </div>
                <div style={{ borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                  <MemberWorkflowProgress
                    workflowInstanceId={instance.id}
                    enrollmentId={instance.enrollment_id}
                    onStepExecute={() => {
                      // Refresh instances after step execution
                      setFilters({
                        workflow_template_id: selectedTemplate,
                        status: selectedStatus
                      });
                    }}
                  />
                </div>
              </div>
            ))
          )}
          {/* Show agentic workflows as simple cards in visual mode */}
          {allInstances.filter(inst => inst.is_agentic).map((instance) => {
            const extInstance = instance as ExtendedWorkflowInstance;
            return (
              <div
                key={`visual-agentic-${instance.id}`}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: '#f5f3ff'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <div>
                    <strong style={{ fontSize: '16px' }}>{instance.workflow_name}</strong>
                    <span className="badge" style={{ backgroundColor: '#8b5cf6', color: 'white', marginLeft: '8px' }}>
                      {t('workflows.ai_agentic', 'AI Agentic')}
                    </span>
                  </div>
                  <span className={`badge ${getStatusBadgeClass(instance.status)}`}>
                    {instance.status}
                  </span>
                </div>
                <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                  {instance.member_name || `Member #${instance.enrollment_id}`}
                </p>
                <p style={{ fontSize: '12px', color: '#9ca3af' }}>
                  {extInstance.employer_name || instance.program_name || '-'}
                </p>
                <div style={{ marginTop: '12px', fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>
                  {t('workflows.managed_by_ai', 'Managed by AI')}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* List View - Original Table */
        <>
          <div className="table-container">
            <table className="mappings-table">
              <thead>
                <tr>
                  <th>{t('workflows.workflow', 'Workflow')}</th>
                  <th>{t('workflows.type', 'Type')}</th>
                  <th>{t('workflows.member', 'Member')}</th>
                  <th>{t('workflows.program', 'Program')}</th>
                  <th>{t('workflows.status_1', 'Status')}</th>
                  <th>{t('workflows.progress', 'Progress')}</th>
                  <th>{t('workflows.started', 'Started')}</th>
                  <th>{t('workflows.duration', 'Duration')}</th>
                  <th>{t('workflows.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {allInstances.map((instance) => {
                  const startedAt = new Date(instance.started_at || instance.created_ts);
                  const completedAt = instance.completed_at ? new Date(instance.completed_at) : new Date();
                  const durationMs = completedAt.getTime() - startedAt.getTime();
                  const durationMinutes = Math.floor(durationMs / 60000);
                  const durationHours = Math.floor(durationMinutes / 60);
                  const durationDays = Math.floor(durationHours / 24);

                  let durationDisplay = '';
                  if (durationDays > 0) {
                    durationDisplay = `${durationDays}d ${durationHours % 24}h`;
                  } else if (durationHours > 0) {
                    durationDisplay = `${durationHours}h ${durationMinutes % 60}m`;
                  } else {
                    durationDisplay = `${durationMinutes}m`;
                  }

                  const extInstance = instance as ExtendedWorkflowInstance;

                  return (
                    <tr
                      key={`${extInstance.is_agentic ? 'agentic' : 'standard'}-${instance.id}`}
                      className={!extInstance.is_agentic && instance.id === focusId ? 'bg-primary/10' : undefined}
                      data-focused={!extInstance.is_agentic && instance.id === focusId ? 'true' : undefined}
                    >
                      <td>
                        <strong>{instance.workflow_name || `Workflow #${instance.workflow_template_id}`}</strong>
                        {extInstance.template_type && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {extInstance.template_type.replace(/_/g, ' ')}
                          </div>
                        )}
                      </td>
                      <td>
                        {extInstance.is_agentic ? (
                          <span className="badge" style={{ backgroundColor: '#8b5cf6', color: 'white' }}>
                            {t('workflows.ai_agentic', 'AI Agentic')}
                          </span>
                        ) : (
                          <span className="badge badge-secondary">
                            {t('workflows.standard', 'Standard')}
                          </span>
                        )}
                      </td>
                      <td>{instance.member_name || `Member #${instance.enrollment_id}`}</td>
                      <td>{instance.program_name || extInstance.employer_name || '-'}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(instance.status)}`}>
                          {instance.status}
                        </span>
                      </td>
                      <td>
                        {instance.current_step_order !== undefined && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div
                              style={{
                                flex: 1,
                                height: '8px',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '4px',
                                overflow: 'hidden',
                              }}
                            >
                              <div
                                style={{
                                  height: '100%',
                                  backgroundColor: getStatusColor(instance.status),
                                  width: `${Math.min(instance.current_step_order * 20, 100)}%`,
                                  transition: 'width 0.3s ease',
                                }}
                              />
                            </div>
                            <small>Step {instance.current_step_order}</small>
                          </div>
                        )}
                      </td>
                      <td>{fmt.dateTime(instance.started_at || instance.created_ts)}</td>
                      <td>{durationDisplay}</td>
                      <td className="actions-cell">
                        {extInstance.is_agentic ? (
                          <span style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic' }}>
                            {t('workflows.managed_by_ai', 'Managed by AI')}
                          </span>
                        ) : (
                          <>
                            {instance.status === 'running' && (
                              <>
                                <button
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-600 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50 transition-colors"
                                  onClick={() => handleStatusChange(instance, 'paused')}
                                >
                                  <Pause className="w-3 h-3" />
                                  {t('workflows.pause', 'Pause')}
                                </button>
                                <button
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                                  onClick={() => handleStatusChange(instance, 'cancelled')}
                                >
                                  <XCircle className="w-3 h-3" />
                                  {t('workflows.cancel', 'Cancel')}
                                </button>
                              </>
                            )}
                            {instance.status === 'paused' && (
                              <button
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-600 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 transition-colors"
                                onClick={() => handleStatusChange(instance, 'running')}
                              >
                                <PlayCircle className="w-3 h-3" />
                                {t('workflows.resume', 'Resume')}
                              </button>
                            )}
                            {instance.status === 'failed' && instance.error_message && (
                              <button
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-600 dark:bg-sky-900/30 dark:text-sky-300 transition-colors"
                                onClick={() => alert(`Error: ${instance.error_message}`)}
                              >
                                {t('workflows.view_error', 'View Error')}
                              </button>
                            )}
                            <button
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300 transition-colors"
                              onClick={() => handlePropagate(instance.workflow_template_id, instance.workflow_name || 'this workflow')}
                              disabled={propagatingId === instance.workflow_template_id}
                            >
                              <RefreshCw className={`w-3 h-3 ${propagatingId === instance.workflow_template_id ? 'animate-spin' : ''}`} />
                              {propagatingId === instance.workflow_template_id ? 'Syncing...' : 'Propagate'}
                            </button>
                            <button
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-600 dark:bg-red-900/30 dark:text-red-300 transition-colors"
                              onClick={() => handleDelete(instance.id, instance.workflow_name || 'this workflow')}
                            >
                              <XCircle className="w-3 h-3" />
                              {t('workflows.delete', 'Delete')}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {allInstances.length === 0 && (
              <div className="empty-state">
                <h3>{t('workflows.no_workflow_instances_found', 'No workflow instances found')}</h3>
                <p>
                  {selectedTemplate || selectedStatus || selectedWorkflowType !== 'all'
                    ? t('workflows.no_instances_match_filters', 'No instances match the current filters.')
                    : t('workflows.workflow_instances_will_appear', 'Workflow instances will appear here when workflows are triggered.')}
                </p>
              </div>
            )}
          </div>

          {allInstances.length > 0 && (
            <div className="pagination">
              <div className="pagination-info">
                {t('workflows.showing_instances', 'Showing')} {Math.min(allInstances.length, pageSize)} {t('workflows.of', 'of')} {combinedTotal} {t('workflows.instances', 'instances')}
                {stats.agentic > 0 && (
                  <span style={{ marginLeft: '8px', color: '#8b5cf6' }}>
                    ({stats.agentic} {t('workflows.ai_agentic', 'AI Agentic')})
                  </span>
                )}
              </div>
              <div className="pagination-controls">
                <button className="btn btn-sm" onClick={() => setPage(1)} disabled={page === 1}>
                  {t('workflows.first', 'First')}
                </button>
                <button className="btn btn-sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
                  {t('workflows.previous', 'Previous')}
                </button>
                <span className="page-number">
                  Page {page} of {totalPages}
                </span>
                <button className="btn btn-sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                  {t('workflows.next', 'Next')}
                </button>
                <button className="btn btn-sm" onClick={() => setPage(totalPages)} disabled={page >= totalPages}>
                  {t('workflows.last', 'Last')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WorkflowInstanceTracker;
